import { v4 as uuid } from 'uuid'

// The renderer reads `statusStyleOverrides` as an object map keyed by the
// status string (`{ "On Track": { color, icon, order, imageId, imageUrl } }`).
// The structured editor renders it as an ordered list of rows so admins can
// drag-to-reorder and have the visual order match the rendered filter pills
// (which sort by `order` descending). These helpers convert between the two
// shapes and own the ordering convention so the editor never has to think
// about it.

export const ORDER_STEP = 10

function isPlainObject (value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function coerceNumber (value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

// Convert the persisted object map into an editor-facing array sorted in pill
// order (highest `order` first). Each row gets a stable `_rowId` so DnD has
// something to key on without touching the persisted shape.
export function statusStylesObjectToRows (overrides) {
  if (!isPlainObject(overrides)) return []
  const entries = Object.entries(overrides)
  const rows = entries.map(([status, rawStyle]) => {
    const style = isPlainObject(rawStyle) ? rawStyle : {}
    return {
      _rowId: uuid(),
      status,
      color: typeof style.color === 'string' ? style.color : '',
      icon: typeof style.icon === 'string' ? style.icon : '',
      order: coerceNumber(style.order),
      imageId: typeof style.imageId === 'string' ? style.imageId : '',
      imageUrl: typeof style.imageUrl === 'string' ? style.imageUrl : ''
    }
  })
  rows.sort((a, b) => (b.order ?? 0) - (a.order ?? 0))
  return rows
}

// Rewrite the `order` field on every row based on its current array index so
// the visual top of the editor maps to the top of the pill list. Higher
// numbers sort first in the renderer's descending sort. We use ORDER_STEP
// gaps to keep the persisted values familiar to admins who previously
// hand-numbered them (the old JsonField used multiples of 10).
export function withDerivedOrder (rows) {
  if (!Array.isArray(rows)) return []
  const total = rows.length
  return rows.map((row, idx) => ({ ...row, order: (total - idx) * ORDER_STEP }))
}

// Convert the editor's array back into the renderer-facing object map.
// Strips empty status keys (a row with no status text is treated as in-flight
// and not persisted) and drops any style fields that are empty so the saved
// blob stays terse and predictable.
export function statusStylesRowsToObject (rows) {
  if (!Array.isArray(rows)) return null
  const result = {}
  rows.forEach((row) => {
    if (!row || typeof row.status !== 'string') return
    const status = row.status.trim()
    if (!status) return

    const style = {}
    if (typeof row.color === 'string' && row.color.trim()) style.color = row.color.trim()
    if (typeof row.icon === 'string' && row.icon.trim()) style.icon = row.icon.trim()
    if (row.order != null) {
      const numericOrder = coerceNumber(row.order)
      if (numericOrder != null) style.order = numericOrder
    }
    if (typeof row.imageId === 'string' && row.imageId.trim()) style.imageId = row.imageId.trim()
    if (typeof row.imageUrl === 'string' && row.imageUrl.trim()) style.imageUrl = row.imageUrl.trim()

    // Last write wins on duplicate status keys — same semantics as a plain
    // object literal. The editor flags duplicates visually so admins notice.
    result[status] = style
  })
  return Object.keys(result).length === 0 ? null : result
}

// Returns a Set of status strings that appear more than once in the editor
// rows. Used to flag duplicates in the UI so admins don't silently lose data
// to the "last write wins" merge.
export function findDuplicateStatusKeys (rows) {
  if (!Array.isArray(rows)) return new Set()
  const seen = new Map()
  rows.forEach((row) => {
    const key = (row?.status || '').trim()
    if (!key) return
    seen.set(key, (seen.get(key) || 0) + 1)
  })
  const duplicates = new Set()
  seen.forEach((count, key) => {
    if (count > 1) duplicates.add(key)
  })
  return duplicates
}

export function makeBlankStatusStyleRow () {
  return {
    _rowId: uuid(),
    status: '',
    color: '',
    icon: '',
    order: null,
    imageId: '',
    imageUrl: ''
  }
}
